import type { RoofTypeName } from '../nativeMounting/types'

/**
 * Maps a Studio roof type to the Van der Valk pitched-roof (on-roof) `claddingType` vendor value sent
 * with the suitability request. There is no user-facing cladding dropdown — cladding is derived from
 * the project's selected roof type. Keep the values in sync with `VANDERVALK_PITCHED_CLADDING_TYPES`
 * in the Django serializer and with `roofTypesCompatibleWithVanDerValkOnRoof`.
 *
 * Vendor enum: STEEL_DECK | CORRUGATED | STANDING_SEAM | TILED | SLATE | MEMBRANE | PLAIN
 */
export const VANDERVALK_ROOF_TYPE_TO_PITCHED_CLADDING: Partial<Record<RoofTypeName, string>> = {
  'Tile Concrete': 'TILED',
  'Tile Clay': 'TILED',
  'Tile Slate': 'SLATE',
  'Membrane EPDM': 'MEMBRANE',
  'Membrane PVC': 'MEMBRANE',
  'Membrane TPO': 'MEMBRANE',
  'Metal Standing Seam': 'STANDING_SEAM',
  Trapezoidal: 'STEEL_DECK',
  'Metal Tin': 'CORRUGATED',
  'Corrugated Fibre Cement': 'CORRUGATED',
}

/** Vendor ``corrugatedType`` for CORRUGATED cladding (roof-derived, not user-editable). */
export type VanDerValkCorrugatedType = 'STEEL' | 'FIBER_CEMENT'

/**
 * Maps Studio roof type → vendor ``corrugatedType`` when cladding is CORRUGATED.
 * Metal Tin → STEEL; Corrugated Fibre Cement → FIBER_CEMENT.
 */
export const VANDERVALK_ROOF_TYPE_TO_CORRUGATED_TYPE: Partial<Record<RoofTypeName, VanDerValkCorrugatedType>> = {
  'Metal Tin': 'STEEL',
  'Corrugated Fibre Cement': 'FIBER_CEMENT',
}

/** Derived pitched `claddingType` for a roof type, or null when the roof type has no mapping. */
export function getVanDerValkPitchedCladdingForRoofType(roofType: unknown): string | null {
  if (typeof roofType !== 'string') {
    return null
  }
  return VANDERVALK_ROOF_TYPE_TO_PITCHED_CLADDING[roofType as RoofTypeName] ?? null
}

/** Derived ``corrugatedType`` for a roof type, or null when the roof is not a corrugated mapping. */
export function getVanDerValkCorrugatedTypeForRoofType(roofType: unknown): VanDerValkCorrugatedType | null {
  if (typeof roofType !== 'string') {
    return null
  }
  return VANDERVALK_ROOF_TYPE_TO_CORRUGATED_TYPE[roofType as RoofTypeName] ?? null
}
